import { format } from "date-fns";
import { Phone, Mail, User, Clock, Copy } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ThreeDotsLoader from "@/components/common/three-dots-loader";
import { useNotification } from "@/features/notifications/hooks/use-notifications";
import { useParams, Link } from "react-router-dom";
import { ALL_REGIONS_CODE, regionPath } from "@/features/regions/constants";
import {
  getStatusIcon,
  getStatusColor,
} from "@/features/notifications/ui/notification-card";
import { Button } from "@/components/ui/button";
import { useCancelSession } from "@/features/sessions/hooks/use-cancel-session";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HasAccess } from "@/components/common/has-access";
import { Badge } from "@/components/ui/badge";
import { CANCELLATION_REASONS } from "@/utils/cancellation-reasons";
import { toast } from "@/hooks/use-toast";
import {
  DISPARITY_LABEL_MAP,
  DisparityReason,
} from "@/utils/disparityLabelMap";

export function NotificationDetails() {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState<string>("");
  const [cancelReason, setCancelReason] = useState<string | undefined>(
    undefined,
  );
  const { mutate: cancelSession } = useCancelSession();

  const isOthersSelected =
    selectedCancelReason === "Others (type reason manually if not listed)";
  const { id, region = ALL_REGIONS_CODE } = useParams();
  const {
    data: notification,
    isLoading,
    error,
    isFetching,
  } = useNotification(id!);

  if (error) return <></>;
  if (isFetching || isLoading || !notification) {
    return (
      <div className="w-full flex justify-center items-center min-h-[400px]">
        <ThreeDotsLoader />
      </div>
    );
  }

  const {
    channel,
    type,
    recipient_email,
    recipient_phone,
    template_id,
    status,
    session,
    user,
    professional,
    sent_at,
    retry_count,
    disparity_check,
    error_message,
  } = notification;

  const formatNotificationType = (value: string) => {
    if (!value) return "UNKNOWN";
    const parts = value.split("_").filter(Boolean);
    const replaced = parts.map((p) => {
      switch (p) {
        case "PROFESSIONAL":
          return "PRO";
        case "CANCELLATION":
          return "CANCEL";
        case "CONFIRMATION":
          return "CONF";
        case "REMINDER":
          return "REM";
        case "TRANSITION":
          return "TRANS";
        default:
          return p;
      }
    });

    return replaced.join(" ").toUpperCase();
  };

  const copyPhoneNumber = async (phone: string) => {
    try {
      const sanitizedPhone = phone.replace(/\D/g, "");
      await navigator.clipboard.writeText(sanitizedPhone);
      toast({
        title: "Copied",
        description: "Phone number copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy phone number",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full p-6">
      <Card className="w-full">
        <CardHeader>
          <div className="flex justify-between items-start w-full">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <CardTitle className="text-2xl font-bold">
                  Notification
                </CardTitle>
                <span
                  title={type}
                  className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap max-w-[260px] truncate bg-slate-100 text-slate-700"
                >
                  {formatNotificationType(type)}
                </span>
              </div>
              <CardDescription className="text-sm text-muted-foreground">
                Sent via{" "}
                <span className="capitalize">{channel.toLowerCase()}</span> •{" "}
                {type}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 min-w-fit">
              {getStatusIcon(status)}
              <span
                className={`px-2 py-0.5 text-xs font-bold rounded ${getStatusColor(
                  status,
                )}`}
              >
                {status}
              </span>
            </div>
          </div>
          <div className="mt-3 text-sm text-muted-foreground justify-between flex gap-4 w-full">
            {sent_at && (
              <div>
                <span className="font-medium">Sent:</span>{" "}
                {format(new Date(sent_at), "PPpp")}
              </div>
            )}
            <div>
              <span className="font-medium">Template ID:</span>{" "}
              <span className="font-bold">{template_id || "—"}</span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div
            className={`grid gap-4 ${
              session ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
            }`}
          >
            <div className="p-4 border rounded">
              <h3 className="font-semibold mb-2">Recipient</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {notification.recipient_type === "USER" && user ? (
                    <Link
                      to={regionPath(region, `/users/${user.id}`)}
                      className="text-blue-500 hover:text-blue-700 transition-colors"
                    >
                      {user.full_name}
                    </Link>
                  ) : notification.recipient_type === "LEAD" &&
                    notification?.lead ? (
                    <Link
                      to={regionPath(region, `/leads/${notification?.lead.id}`)}
                      className="text-blue-500 hover:text-blue-700 transition-colors"
                    >
                      {notification?.lead.full_name}
                    </Link>
                  ) : (
                    <span>{notification.professional?.name || "Unknown"}</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>{recipient_phone}</span>
                  {recipient_phone && (
                    <button
                      onClick={() => copyPhoneNumber(recipient_phone)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Copy phone number"
                    >
                      <Copy className="h-3 w-3 text-gray-500" />
                    </button>
                  )}
                </div>

                {recipient_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {recipient_email}
                  </div>
                )}
              </div>
            </div>

            {session && (
              <div className="p-4 border rounded">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold">Session Info</h3>
                    <Badge
                      variant={
                        session.status === "active" ? "active" : "destructive"
                      }
                    >
                      {session.status.toUpperCase()}
                    </Badge>
                  </div>
                  <HasAccess permission="write">
                    {session.status === "active" && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setCancelDialogOpen(true);
                        }}
                      >
                        Cancel Session
                      </Button>
                    )}
                  </HasAccess>
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  {professional && (
                    <div>
                      <span className="font-medium">Professional:</span>{" "}
                      {professional.name || professional.id}
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <div>
                      <span className="font-medium">Session:</span>{" "}
                      <Link
                        to={regionPath(region, `/sessions/${session.id}`)}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                      >
                        {session.name || session.id}
                      </Link>
                    </div>
                  </div>

                  {session.start_time && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4 inline-block mr-1" />
                      {format(new Date(session.start_time), "PPpp")}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RH-801: surface why a send failed (the card shows it; the detail
              page didn't). Same field the queue handlers write on failure. */}
          {error_message && (
            <div className="border border-red-200 rounded p-4 bg-red-50">
              <h3 className="font-semibold text-red-800 mb-1">Error</h3>
              <p className="text-sm text-red-700 break-words">{error_message}</p>
            </div>
          )}

          {disparity_check && (
            <div className="border rounded p-4 bg-yellow-50">
              <h3 className="font-semibold text-yellow-800 mb-2">
                Disparity Check
              </h3>

              <div className="flex items-center text-sm mb-4">
                <span
                  className={`text-sm font-medium px-2 py-1 rounded ${
                    disparity_check.is_match
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {disparity_check.is_match ? "Match" : "Mismatch"}
                </span>
              </div>

              {!disparity_check.is_match &&
                Array.isArray(disparity_check.reasons) && (
                  <ul className="flex flex-col md:flex-row md:flex-wrap gap-4">
                    {disparity_check.reasons.map(
                      (reason: DisparityReason, index: number) => {
                        const labels = DISPARITY_LABEL_MAP[reason.type] ?? {
                          user: "User Value",
                          professional: "Professional Value",
                        };

                        return (
                          <li
                            key={index}
                            className="flex-1 min-w-[300px] md:max-w-[calc(50%-0.5rem)] border bg-white p-4 rounded-md shadow-sm space-y-2"
                          >
                            <div className="flex justify-between items-center">
                              <p className="font-semibold text-gray-800">
                                {reason.type.replace(/_/g, " ")}
                              </p>
                            </div>

                            <p className="text-sm text-gray-700 min-h-[2.5rem] leading-snug">
                              {reason.message}
                            </p>

                            <div className="flex gap-2 text-sm mt-2">
                              <div className="bg-gray-50 rounded px-3 py-2 w-1/2 h-full">
                                <span className="block text-gray-400 text-[11px] uppercase tracking-wide mb-1">
                                  {labels.user}
                                </span>
                                <p className="text-gray-800 break-words">
                                  {Array.isArray(reason.userValue)
                                    ? reason.userValue.join(", ")
                                    : String(reason.userValue)}
                                </p>
                              </div>

                              <div className="bg-gray-50 rounded px-3 py-2 w-1/2">
                                <span className="block text-gray-400 text-[11px] uppercase tracking-wide mb-1">
                                  {labels.professional}
                                </span>
                                <p className="text-gray-800 break-words">
                                  {Array.isArray(reason.professionalValue)
                                    ? reason.professionalValue.join(", ")
                                    : String(reason.professionalValue)}
                                </p>
                              </div>
                            </div>
                          </li>
                        );
                      },
                    )}
                  </ul>
                )}
            </div>
          )}
          <Dialog
            open={cancelDialogOpen}
            onOpenChange={(open) => {
              setCancelDialogOpen(open);
              if (!open) {
                // Reset states when dialog closes
                setSelectedCancelReason("");
                setCancelReason(undefined);
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  Cancel {session?.invitee_full_name} Session
                </DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Select cancellation reason
                  </label>
                  <Select
                    value={selectedCancelReason}
                    onValueChange={setSelectedCancelReason}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {CANCELLATION_REASONS.map((reason) => (
                        <SelectItem key={reason} value={reason}>
                          {reason}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isOthersSelected && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Please provide a reason for cancellation
                    </label>
                    <Textarea
                      placeholder="Type your reason here"
                      value={cancelReason ?? ""}
                      onChange={(e) =>
                        setCancelReason(e.target.value || undefined)
                      }
                      className="min-h-[100px]"
                    />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCancelDialogOpen(false);
                    setSelectedCancelReason("");
                    setCancelReason(undefined);
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    const finalReason = isOthersSelected
                      ? cancelReason
                      : selectedCancelReason;

                    cancelSession({
                      source_id: session?.source_id,
                      reason: finalReason,
                    });
                    setSelectedCancelReason("");
                    setCancelReason(undefined);
                    setCancelDialogOpen(false);
                  }}
                  disabled={
                    !selectedCancelReason || (isOthersSelected && !cancelReason)
                  }
                >
                  Confirm Cancellation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
}
